import Link from 'next/link';
import { useEffect, useState } from 'react';

import { Attacks, Warrior } from '~/../api/common.types';
import Paragraph from '~/components/common/Paragrah';
import { BattleChooseAttack } from '~/components/war/battle/BattleChooseAttack';
import { useGameAuthentication } from '~/contextProviders/GameAuthenticationProvider';
import { useGameContext } from '~/contextProviders/GameContextProvider';
import { useSetAttack } from '~/hooks/game/useSetAttack';
import { calculateAttackBonus, calculateBatchAttack } from '~/utils/utils';

import Button from '../../common/Button';


export default function WarriorControls({
  warrior,
  opponent,
  onShare,
  shareClicked,
  shareURL,
  onSelectAttack,
}: {
  warrior: Warrior;
  opponent: Warrior;
  onShare?: () => void;
  shareClicked: boolean;
  shareURL: string;
  onSelectAttack: (attack: string | undefined) => void;
}) {
  const [attackIsEditing, setAttackIsEditing] = useState(false); // Tracks if the set attack is being edited
  const [showAttackControls, setShowAttackControls] = useState(false); // Tracks if the set attack controls are being shown
  const [attack, setAttack] = useState(
    warrior.set_attack ? warrior.set_attack.toLowerCase() : null
  ); // lower case to account for image paths
  const [overlayVisible, setOverlayVisible] = useState(!!warrior.set_attack); // Tracks if the attack selector is visible
  const [attackSubmitted, setAttackSubmitted] = useState(false);
  const [notVerified, setNotVerified] = useState(false);
  const { accessToken } = useGameAuthentication();
  const { mutateAsync: setAttackMutation, isLoading } = useSetAttack();
  const {
    setPlayMusic,
    areResultsReady,
    contextId,
    battle,
    isAttacksActive,
    tournamentParam,
    batchAttack,
    totalBatchAttackSuccess,
    setTotalBatchAttackSuccess,
  } = useGameContext();
  const isPeaceful = tournamentParam === 'peaceful';

  let attackBonus = 0;
  if (attack) {
    attackBonus = calculateAttackBonus(attack, warrior, tournamentParam, battle);
  }

  useEffect(() => {
    if (batchAttack) {
      const attackBatchCalculated = calculateBatchAttack(batchAttack, warrior, opponent, tournamentParam, battle);
      if (attackBatchCalculated && !totalBatchAttackSuccess) {
        selectAttack(attackBatchCalculated);
        submitAttack(attackBatchCalculated);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchAttack, totalBatchAttackSuccess]);


  const selectAttack = (attackVal: Attacks) => {
    console.log('attackVal', attackVal);
    setAttack(attackVal);
    onSelectAttack(attackVal);
    setOverlayVisible(true);
    setShowAttackControls(true);
  };


  const submitAttack = async (submittedAttack) => {
    const atackArg = submittedAttack || attack;
    if (!atackArg) return;
    setAttackMutation(
      {
        warriorId: warrior.warrior_id,
        contextId: contextId,
        battleId: battle,
        attack: atackArg,
        token: accessToken,
      },
      {
        onSuccess: (attackVal) => {
          setAttack(attackVal);
          onSelectAttack(attackVal);
          setShowAttackControls(false);
          setTotalBatchAttackSuccess(true);
          setPlayMusic(true);
        },
        onError: (error: any) => {
          if (error?.response?.data?.errors) {
            const err = error.response.data['errors'][0];
            // handle not verified wallet
            if (err?.code === 'context:wallet-not-registered') {
              setNotVerified(true);
              return;
            }
          }
          setAttack(null);
          onSelectAttack(undefined);
          setOverlayVisible(false);
          setAttackSubmitted(false);
          // eslint-disable-next-line no-alert
          alert(
            'Attack was not submitted, please refresh and try again'
          );
          setShowAttackControls(false);
        },
      }
    );
  };


  if (notVerified) {
    return (
      <div className="warrior__attack">
        <div className="battle-choose-attack">
          <Paragraph>
          This wallet is not registered for the tournament.
            <div className="warrior__attack__confirm warrior__attack__confirm--active">
              <Link href="/tournaments/signup" passHref>
                <button className="warrior__attack__confirm__button primary">
                Register for the tournament
                </button>
              </Link>
            </div>
          </Paragraph>
        </div>
      </div>

    );
  }

  // console.log('attack?.toLowerCase()', attack?.toLowerCase());
  return (
    <div className="warrior__attack">
      <BattleChooseAttack
        warrior={warrior}
        onSelect={(attackVal: Attacks) => {
          selectAttack(attackVal);
          // setAttack(attackVal);
          // onSelectAttack(attackVal);
          // setOverlayVisible(true);
          // setShowAttackControls(true);
        }}
        showCancel={attackIsEditing}
        onCancel={() => {
          setOverlayVisible(true);
          setShowAttackControls(false);
          setAttackIsEditing(false);
        }}
      />
      <div
        className={`warrior__attack__confirm ${
          overlayVisible && 'warrior__attack__confirm--active'
        }`}
      >
        {showAttackControls && (
          <>
            <img
              src={
                isPeaceful ?
                  `/img/peaceful/${attack?.toLowerCase()}.png` :
                  `/img/syndicate.${attack?.toLowerCase()}.png`
              }
              alt={attack || 'attack image'}
              className="warrior__attack__image"
            />
            <Button
              className="warrior__attack__confirm__button primary"
              label={
                isLoading ? (
                  'Saving...'
                ) : (
                  <span>
                    Submit <span className="attack-name">{attack}</span> attack
                  </span>
                )
              }
              onClick={() => {
                if (!attack) return;
                submitAttack(attack);
              }}
              enabled={!isLoading && !attackSubmitted ? 1 : 0}
            />
            <Button
              className="warrior__attack__confirm__button secondary"
              label="Cancel"
              onClick={() => {
                setOverlayVisible(false);
                onSelectAttack(undefined);
                setAttackSubmitted(false);
              }}
            />
          </>
        )}
        {!showAttackControls && (
          <>
            <img
              src={
                isPeaceful ?
                  `/img/peaceful/${attack?.toLowerCase()}.png` :
                  `/img/syndicate.${attack?.toLowerCase()}.png`
              }
              alt={attack || 'attack image'}
              className="warrior__attack__image"
            />
            <Paragraph className="paragraph--secondary">
              You selected{' '}
              <span className="attack-name">
                {attack || warrior.set_attack}
              </span>{' '}
              attack
              {attackBonus > 0 && ` with +${attackBonus} HP bonus`}
            </Paragraph>
            {isAttacksActive && (
              <Button
                className="warrior__attack__confirm__button secondary"
                label="Change Attack"
                onClick={() => {
                  setOverlayVisible(false);
                  setAttackIsEditing(true);
                }}
                disabled={!isAttacksActive}
              />
            )}
            {areResultsReady && <span>Waiting on battle results</span>}
            {/* TODO: Figure out why image is not downloaded */}
            {shareURL ? (
              <a
                className="warrior__attack__confirm__button primary"
                download
                href={shareURL}
                target="_blank"
                rel="noreferrer"
              >
                Download Image
              </a>
            ) : (
              <Button
                className="warrior__attack__confirm__button primary"
                style={{ display: 'none' }}
                label={
                  shareClicked ? 'Generating preview...' : 'Share your matchup'
                }
                onClick={onShare}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
